/**
 * Step-Up Verification Page
 *
 * Displayed when the system determines that additional verification is
 * required based on fraud signals, repeat-attempt detection, or device risk.
 * Shows step-up requirements and collects additional verification material.
 *
 * @module pages/StepUpPage
 */

import React, { useCallback, useMemo, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import type { ApiClientError } from '@noxverify/api-client';
import { StepUpBanner } from '../components/StepUpBanner';
import { AdditionalDocUpload } from '../components/AdditionalDocUpload';
import { StepIndicator, type Step } from '../components/StepIndicator';
import { Footer } from '../components/Footer';
import { getApiClient, uploadFileToSignedUrl } from '../lib/api';
import type { ApplicantContext } from '../modules/context/context-loader';

/** Step-up action types that the page can handle. */
type StepUpAction =
  | 'additional_document'
  | 'selfie_reverify'
  | 'video_verification'
  | 'manual_review'
  | 'enhanced_screening';

interface StepUpPageProps {
  context: ApplicantContext;
}

export function StepUpPage({ context }: StepUpPageProps) {
  const navigate = useNavigate();
  const { t } = useTranslation('portal');
  const { caseId } = useParams<{ caseId: string }>();
  const [searchParams] = useSearchParams();

  const stepUpSteps = useMemo<Step[]>(
    () => [
      { key: 'start', label: t('stepIndicator.steps.start') },
      { key: 'details', label: t('stepIndicator.steps.details') },
      { key: 'documents', label: t('stepIndicator.steps.documents') },
      { key: 'selfie', label: t('stepIndicator.steps.selfie') },
      { key: 'step-up', label: t('stepIndicator.steps.stepUp') },
      { key: 'complete', label: t('stepIndicator.steps.complete') },
    ],
    [t],
  );

  // Parse step-up actions from query string (comma-separated)
  const actionsParam = searchParams.get('actions') ?? '';
  const reason = searchParams.get('reason') ?? undefined;
  const actions: StepUpAction[] = actionsParam
    .split(',')
    .filter(Boolean) as StepUpAction[];

  // Track completed steps
  const [completedSteps, setCompletedSteps] = useState<Set<string>>(new Set());
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Determine which user-facing actions are still pending
  const userActions = actions.filter(
    (a) => a !== 'manual_review' && a !== 'enhanced_screening',
  );
  const pendingUserActions = userActions.filter(
    (a) => !completedSteps.has(a),
  );
  const allUserStepsComplete = pendingUserActions.length === 0;

  const handleDocUpload = useCallback(
    async (file: File) => {
      if (!caseId) return;

      setUploading(true);
      setError(null);

      try {
        const client = getApiClient();

        // Step 1: Create a submission set for the step-up document.
        const ssResponse = await client.applicant.createSubmissionSet(caseId);
        const submissionSetId = ssResponse.data.id;

        // Step 2: Request an upload slot for the additional document.
        const uploadResponse = await client.applicant.requestUploadUrl(
          submissionSetId,
          {
            evidenceType: 'other',
            mimeType: file.type,
            fileName: file.name,
          },
        );

        // Step 3: PUT the file bytes to the signed URL.
        await uploadFileToSignedUrl(uploadResponse.data.signedUrl, file);

        // Step 4: Confirm the upload is complete.
        await client.applicant.confirmUpload(uploadResponse.data.uploadId, {
          fileSizeBytes: file.size,
        });

        // Only mark the step complete after a fully successful upload.
        setCompletedSteps((prev) => new Set([...prev, 'additional_document']));
      } catch (err: unknown) {
        const apiErr = err as ApiClientError;
        setError(apiErr.message ?? t('stepUp.uploadFailed'));
      } finally {
        setUploading(false);
      }
    },
    [caseId, t],
  );

  const handleContinue = useCallback(() => {
    if (!caseId) return;
    // Navigate back to the confirmation page once all user steps are done
    navigate(`/case/${caseId}/confirmation`);
  }, [caseId, navigate]);

  const handleReselfie = useCallback(() => {
    if (!caseId) return;
    // Navigate to selfie page for re-verification
    navigate(`/case/${caseId}/selfie?stepup=true`);
  }, [caseId, navigate]);

  return (
    <div className="flex min-h-screen flex-col bg-surface">
      {/* Header */}
      <header className="border-b border-hairline bg-surface">
        <div className="mx-auto flex max-w-3xl items-center gap-4 px-4 py-4">
          {context.branding.logoUrl && (
            <img
              src={context.branding.logoUrl}
              alt={t('app.logoAlt', { projectSlug: context.projectSlug })}
              className="h-8"
            />
          )}
          <h1
            className="text-lg font-semibold"
            style={{ color: 'var(--nv-primary-color)' }}
          >
            {t('stepUp.headerTitle')}
          </h1>
        </div>
      </header>

      {/* Step indicator */}
      <div className="mx-auto w-full max-w-3xl px-4">
        <StepIndicator currentStep="step-up" steps={stepUpSteps} />
      </div>

      {/* Main content */}
      <main className="flex-1">
        <div className="mx-auto max-w-3xl space-y-6 px-4 py-8">
          {/* Banner explaining why step-up is needed */}
          <StepUpBanner actions={actions} reason={reason} />

          {/* Additional document upload */}
          {actions.includes('additional_document') && (
            <div>
              {completedSteps.has('additional_document') ? (
                <div className="rounded-lg border border-primary/30 bg-primary-tint px-4 py-3">
                  <div className="flex items-center gap-2">
                    <svg
                      className="h-5 w-5 text-primary"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                      strokeWidth={2}
                      aria-hidden="true"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                      />
                    </svg>
                    <span className="text-sm font-medium text-primary-hover">
                      {t('stepUp.uploadedSuccessfully')}
                    </span>
                  </div>
                </div>
              ) : (
                <AdditionalDocUpload
                  caseId={caseId ?? ''}
                  onUploadComplete={handleDocUpload}
                  uploading={uploading}
                  error={error}
                />
              )}
            </div>
          )}

          {/* Selfie re-verification */}
          {actions.includes('selfie_reverify') &&
            !completedSteps.has('selfie_reverify') && (
              <div className="rounded-lg border border-hairline bg-white p-6">
                <h3 className="mb-1 text-lg font-semibold text-ink">
                  {t('stepUp.selfieReverify.title')}
                </h3>
                <p className="mb-4 text-sm text-ink-muted">
                  {t('stepUp.selfieReverify.description')}
                </p>
                <button
                  type="button"
                  onClick={handleReselfie}
                  className="inline-flex items-center gap-2 rounded-md px-6 py-2.5 text-sm font-semibold text-white transition-opacity hover:brightness-90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-surface"
                  style={{
                    backgroundColor: 'var(--nv-primary-color)',
                    '--tw-ring-color': 'var(--nv-primary-color)',
                  } as React.CSSProperties}
                >
                  {t('stepUp.selfieReverify.button')}
                </button>
              </div>
            )}

          {/* Video verification */}
          {actions.includes('video_verification') &&
            !completedSteps.has('video_verification') && (
              <div className="rounded-lg border border-hairline bg-white p-6">
                <h3 className="mb-1 text-lg font-semibold text-ink">
                  {t('stepUp.videoVerification.title')}
                </h3>
                <p className="mb-4 text-sm text-ink-muted">
                  {t('stepUp.videoVerification.description')}
                </p>
                <p className="text-xs text-ink-muted">
                  {t('stepUp.videoVerification.pendingNote')}
                </p>
              </div>
            )}

          {/* Error display */}
          {error && (
            <div
              role="alert"
              className="rounded-lg border border-danger/30 bg-danger-tint px-4 py-3 text-sm text-danger"
            >
              {error}
            </div>
          )}

          {/* Continue button */}
          <div className="flex justify-end pt-4">
            <button
              type="button"
              onClick={handleContinue}
              disabled={!allUserStepsComplete}
              className="inline-flex items-center gap-2 rounded-md px-8 py-3 font-semibold text-white transition-opacity hover:brightness-90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-surface disabled:cursor-not-allowed disabled:opacity-50"
              style={{
                backgroundColor: 'var(--nv-primary-color)',
                '--tw-ring-color': 'var(--nv-primary-color)',
              } as React.CSSProperties}
            >
              {allUserStepsComplete ? t('stepUp.continue') : t('stepUp.completeAll')}
            </button>
          </div>
        </div>
      </main>

      <Footer branding={context.branding} />
    </div>
  );
}
